<template>
  <div v-if="!isExistingSectionOpen" class="tw-mb-6">
    <SfBannerInline variant="ai" :align="FlexAlign.START">
      <template #title>
        <span v-dompurify-html="info1" class="tw-inline-block tw-mb-[18px]" />
        <span v-dompurify-html="info2" />
      </template>
    </SfBannerInline>
  </div>

  <div class="tw-mb-6">
    <SfDocumentsUploader
      variant="seamless"
      :disabled="reachedMaxFiles"
      :show-controls="false"
      :show-exports="false"
      :show-file-manage-area="false"
      :on-file-uploaded="onFileUploaded"
      :on-file-removed="onFileRemoved"
      :on-file-attached="onFilesAttached"
      :on-file-added="onFileAdded"
      :on-file-upload-progress="onFileUploadProgress"
      @select-existing-toggle="handleExistingSelectToggle"
    >
      <template #post-script>
        <span class="tw-text-sm" :class="maxFileMessageClasses">
          {{ maxFileMessage }}
        </span>
      </template>
    </SfDocumentsUploader>
  </div>

  <div v-for="id in allDocumentIds" :key="id" class="tw-mb-2">
    <DocumentCard
      :document="documents[id]"
      :progress="uploadProgress?.[id]"
      @delete="documentStore.deleteDocument(id)"
    />
  </div>
</template>

<script setup lang="ts">
import type {
  RawExistingDocument,
  RawExistingPermanentDocument,
} from '@app/shared/types/sf-data'
import { isRawExistingDocument } from '@app/shared/predicates'
import type { FileWithProgress } from '@app/shared/vue/components/direct-upload/types'

import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import DocumentCard from './document-card.vue'
import SfDocumentsUploader from '@app/application/reconciliation/sections/documents-uploader.vue'
import { FlexAlign } from '@app/shared/constants'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import {
  formatExistingDocument,
  formatExistingPermanentDocument,
  formatFile,
} from '@app/utils/direct-upload'

import { useOCRDocuments } from '../store/use-ocr-documents'

const I18n = useI18n()

const isExistingSectionOpen = ref(false)

const info1 = I18n.t('ai_ocr_requests.steps.messages.description_line_1')
const info2 = I18n.t('ai_ocr_requests.steps.messages.description_line_2')

const documentStore = useOCRDocuments()

const {
  allDocumentIds,
  documents,
  maxDocumentNumber,
  reachedMaxFiles,
  uploadProgress,
} = storeToRefs(documentStore)

const maxFileMessage = computed(() => {
  return reachedMaxFiles.value
    ? I18n.t('ai_ocr_requests.steps.max_files_reached', {
        count: maxDocumentNumber.value,
      })
    : I18n.t('ai_ocr_requests.steps.max_files', {
        count: maxDocumentNumber.value,
      })
})

const maxFileMessageClasses = computed(() => {
  return {
    'tw-text-sf-danger-darker': reachedMaxFiles.value,
  }
})

function handleExistingSelectToggle(value: boolean): void {
  isExistingSectionOpen.value = value
}

function onFileRemoved(uuid: string, _length: number): void {
  documentStore.deleteDocument(uuid)
}

function onFileUploaded(
  file: FileWithProgress,
  uuid: string,
  _length: number
): void {
  documentStore.addDocument(formatFile(uuid, file))
}

function onFileAdded(file: FileWithProgress, uuid: string): void {
  documentStore.addDocument(formatFile(uuid, file))
}

function onFilesAttached(
  files: Array<RawExistingDocument | RawExistingPermanentDocument>
): void {
  documentStore.addDocuments(
    files.map((file) =>
      isRawExistingDocument(file)
        ? formatExistingDocument(file)
        : formatExistingPermanentDocument(file)
    )
  )
}

function onFileUploadProgress(
  uuid: string,
  file: FileWithProgress,
  progress: string
): void {
  documentStore.updateUploadProgress({
    uuid,
    file: formatFile(uuid, file),
    progress,
  })
}
</script>
